import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { PasswordInput } from "@/components/PasswordInput";
import { CredentialsManager } from "@/components/CredentialsManager";
import { AttendanceWindowsEditor } from "@/components/AttendanceWindowsEditor";
import { AttendanceWindowsMatrix } from "@/components/AttendanceWindowsMatrix";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { SettingsGate, isSettingsUnlocked, unlockSettings, lockSettings as gateLockSettings } from "@/components/SettingsGate";
import {
  CloudUpload,
  ShieldCheck,
  ShieldX,
  RotateCw,
  Trash2,
  FileJson,
  Info,
  Plus,
  X,
  Lock,
  KeyRound,
  Tag,
  ScanFace,
  Building2,
  Users,
  Database,
  TreePine,
  HardHat,
  IdCard,
  Layers,
  ChevronLeft,
  CalendarRange,
  CalendarOff,
  Settings as SettingsIcon,
  DatabaseBackup,
  Sparkles,
  Coffee,
  FileText,
  Wallet,
  CalendarX2,
  ClipboardCheck,
  HardDrive,
  UserCog,
  Activity,
} from "lucide-react";
import { fmtDateTime } from "@/lib/utils-app";

const GATE_PASSWORD = "itcot1979";
const GATE_KEY = "tnpm_settings_unlocked";
// Password gate live again — protects Settings + Supervisor Schedules +
// Cleaning Questions + Storage. Shared sessionStorage flag means one
// unlock per tab covers all four screens.
const GATE_ENABLED = true;

/* ------------------------------------------------------------ TILE STYLES */
// Unified light tile — white surface, slate border, amber-tinted icon chip.
// Matches the AttendanceHubPage tiles so the admin area feels cohesive while
// the sidebar (dark navy) handles the heavy contrast.
function Tile({ icon: Icon, label, sublabel, onClick, to, testid, status }) {
  const inner = (
    <div
      className="group relative flex h-32 w-full flex-col items-center justify-center gap-2.5 overflow-hidden rounded-xl border border-slate-200 bg-white px-3 py-3 text-slate-800 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-md hover:shadow-amber-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
    >
      {/* subtle amber glow accent on hover */}
      <span
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/70 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        aria-hidden
      />
      <div className="grid h-10 w-10 place-items-center rounded-lg border border-amber-200 bg-amber-50 transition-colors duration-200 group-hover:border-amber-300 group-hover:bg-amber-100">
        <Icon className="h-5 w-5 text-amber-600" strokeWidth={1.75} />
      </div>
      <div className="text-center">
        <div className="text-[13px] font-semibold leading-tight text-slate-900">{label}</div>
        {sublabel && (
          <div className="mt-1 text-[9px] font-medium uppercase tracking-wider text-slate-500">{sublabel}</div>
        )}
      </div>
      {status && (
        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">
          {status}
        </span>
      )}
    </div>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} aria-label={label}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-label={label} className="w-full text-left">
      {inner}
    </button>
  );
}

/* ----------------------------------------------------------- MAIN COMPONENT */
export default function SettingsPage() {
  const [unlocked, setUnlocked] = useState(() => isSettingsUnlocked());
  const [pwd, setPwd] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [funBox, setFunBox] = useState(null); // 'fortune' | 'coffee' | null
  const [fortuneIdx, setFortuneIdx] = useState(() => Math.floor(Math.random() * 100));

  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [catBusy, setCatBusy] = useState(false);

  // section drill-down. null = home tile grid. "data" | "brand" | etc.
  // Persisted to URL so navigating away (e.g. to /admin/parks) and back
  // restores the same section view instead of resetting to the home grid.
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get("section");
  const setSection = (next) => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (next) sp.set("section", next);
        else sp.delete("section");
        return sp;
      },
      { replace: false }
    );
  };

  const loadAll = async () => {
    const [s] = await Promise.all([
      api.get("/settings"),
    ]);
    setSettings(s.data);
  };

  useEffect(() => {
    if (unlocked) loadAll();
  }, [unlocked]);

  // ----- gate
  if (!unlocked && GATE_ENABLED) {
    const tryUnlock = (e) => {
      e?.preventDefault?.();
      if (pwd === GATE_PASSWORD) {
        unlockSettings();
        setUnlocked(true);
        setPwdError("");
      } else {
        setPwdError("Incorrect password");
      }
    };
    return (
      <AppShell title="Settings" subtitle="Restricted area">
        <Card className="max-w-md">
          <CardContent className="flex flex-col gap-3 p-6">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              <div>
                <div className="font-display text-base font-semibold">Settings is password protected</div>
                <div className="text-xs text-muted-foreground">Enter the settings password to continue.</div>
              </div>
            </div>
            <form onSubmit={tryUnlock} className="mt-2 flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">Settings password</Label>
                <PasswordInput
                  value={pwd}
                  onChange={(e) => { setPwd(e.target.value); setPwdError(""); }}
                  autoFocus
                  data-testid="settings-gate-input"
                  testIdToggle="settings-gate-toggle"
                />
                {pwdError && <div className="text-xs text-red-600">{pwdError}</div>}
              </div>
              <Button type="submit" data-testid="settings-gate-submit"><KeyRound className="mr-2 h-4 w-4" />Unlock</Button>
            </form>
          </CardContent>
        </Card>
      </AppShell>
    );
  }

  if (!settings) {
    return (
      <AppShell title="Settings" subtitle="Operational thresholds, branding, storage and credentials">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </AppShell>
    );
  }

  /* ------------------------------------------------------ shared mutations */
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        visit_interval_hours: Number(settings.visit_interval_hours) || 2,
        face_match_threshold: Number(settings.face_match_threshold) || 0.6,
        sunday_absent_threshold: Math.max(0, Math.min(6, Number(settings.sunday_absent_threshold ?? 4))),
        hourly_selfie_enabled: settings.hourly_selfie_enabled !== false,
        hourly_selfie_default_interval_minutes: Math.max(5, Math.min(180,
          Number(settings.hourly_selfie_default_interval_minutes ?? 60))),
        hourly_selfie_window_start: settings.hourly_selfie_window_start || "08:00",
        hourly_selfie_window_end: settings.hourly_selfie_window_end || "18:00",
        supervisor_gps_tracking_interval_minutes: Number(settings.supervisor_gps_tracking_interval_minutes ?? 5),
      });
      setSettings(data);
      await loadAll();
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };



  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    setCatBusy(true);
    try {
      const { data } = await api.post("/settings/categories", { name });
      setSettings(data);
      setNewCategory("");
      toast.success(`Added "${name}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCatBusy(false); }
  };

  const removeCategory = async (name) => {
    if (!window.confirm(`Remove category "${name}"? Existing employees keep their current category value.`)) return;
    try {
      const { data } = await api.delete(`/settings/categories/${encodeURIComponent(name)}`);
      setSettings(data);
      toast.success("Removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const lock = () => {
    gateLockSettings();
    setUnlocked(false);
    setPwd("");
  };

  /* ----------------------------------------------------- TILES (home grid) */
  const tiles = [
    {
      id: "data",
      label: "Data Management",
      sublabel: "Parks · Supervisors · Workers",
      icon: Database,
      theme: "emerald",
      testid: "settings-tile-data",
    },
    {
      id: "windows",
      label: "Attendance Windows",
      sublabel: "Per park time slots",
      icon: CalendarRange,
      theme: "indigo",
      testid: "settings-tile-windows",
    },
    {
      id: "holidays",
      label: "Holidays",
      sublabel: "Counted as Present",
      icon: CalendarOff,
      theme: "rose",
      testid: "settings-tile-holidays",
      to: "/admin/holidays",
    },
    {
      id: "supervisor-schedules",
      label: "Supervisor Schedules",
      sublabel: "Weekly park visit roster",
      icon: CalendarRange,
      theme: "indigo",
      testid: "settings-tile-supervisor-schedules",
      to: "/admin/supervisor-schedules",
    },
    {
      id: "supervisor-incharge",
      label: "Supervisor In-charge",
      sublabel: "Backup coverage during absence",
      icon: UserCog,
      theme: "sky",
      testid: "settings-tile-supervisor-incharge",
      to: "/admin/supervisor-incharge",
    },
    {
      id: "cleaning-questions",
      label: "Cleaning Questions",
      sublabel: "Before / After CMS",
      icon: ClipboardCheck,
      theme: "emerald",
      testid: "settings-tile-cleaning-questions",
      to: "/admin/cleaning-questions",
    },
    {
      id: "storage",
      label: "Storage",
      sublabel: "Disk & photo cleanup",
      icon: HardDrive,
      theme: "slate",
      testid: "settings-tile-storage",
      to: "/admin/storage",
    },
    {
      id: "deploy-snapshot",
      label: "Deploy Snapshot",
      sublabel: "DB carry-over to production",
      icon: Database,
      theme: "violet",
      testid: "settings-tile-deploy-snapshot",
      to: "/admin/deploy-snapshot",
    },
    {
      id: "face",
      label: "Face Match Threshold",
      sublabel: "Strictness control",
      icon: ScanFace,
      theme: "teal",
      testid: "settings-tile-face",
    },
    {
      id: "categories",
      label: "Worker Categories",
      sublabel: "Sweeper, Driver, …",
      icon: Tag,
      theme: "orange",
      testid: "settings-tile-categories",
    },
    {
      id: "credentials",
      label: "User Credentials",
      sublabel: "Reset passwords",
      icon: Users,
      theme: "violet",
      testid: "settings-tile-credentials",
    },
    {
      id: "report-groups",
      label: "Report Groups",
      sublabel: "Monthly DOCX park groupings",
      icon: FileText,
      theme: "indigo",
      testid: "settings-tile-report-groups",
      to: "/admin/report-groups",
    },
    {
      id: "payroll-rates",
      label: "Payroll Rates",
      sublabel: "Salary components per role",
      icon: Wallet,
      theme: "amber",
      testid: "settings-tile-payroll-rates",
      to: "/admin/payroll-rates",
    },
    {
      id: "salary-policy",
      label: "Salary Policy",
      sublabel: "Sunday-leave rule",
      icon: CalendarX2,
      theme: "rose",
      testid: "settings-tile-salary-policy",
    },
    {
      id: "hourly-selfie",
      label: "Hourly Self-Attendance",
      sublabel: "ITCOT supervisor selfies",
      icon: ScanFace,
      theme: "sky",
      testid: "settings-tile-hourly-selfie",
    },
    {
      id: "supervisor-gps-tracking",
      label: "Supervisor Live Location Tracking",
      sublabel: "Interval (5, 10, 15, 30 min)",
      icon: Activity,
      theme: "emerald",
      testid: "settings-tile-supervisor-gps-tracking",
    },
    {
      id: "fortune",
      label: "Park-Master's Fortune",
      sublabel: "A new wisdom on every tap",
      icon: Sparkles,
      theme: "rose",
      testid: "settings-tile-fortune",
      funBox: "fortune",
    },
    {
      id: "coffee",
      label: "Coffee Break Counter",
      sublabel: "How hard your team is grinding today",
      icon: Coffee,
      theme: "amber",
      testid: "settings-tile-coffee",
      funBox: "coffee",
    },
  ];

  /* -------------------------------------------------- HOME (tile grid view) */
  if (!section) {
    return (
      <AppShell
        title="Settings"
        subtitle="Tap a tile to manage that area"
        action={GATE_ENABLED ? (
          <Button variant="outline" size="sm" onClick={lock} data-testid="settings-relock-button">
            <Lock className="mr-1.5 h-3.5 w-3.5" />Lock
          </Button>
        ) : null}
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid="settings-tile-grid">
          {tiles.map((t) => (
            <Tile
              key={t.id}
              icon={t.icon}
              label={t.label}
              sublabel={t.sublabel}
              theme={t.theme}
              testid={t.testid}
              status={t.status}
              to={t.to}
              onClick={t.to ? undefined : (t.funBox ? () => setFunBox(t.funBox) : () => setSection(t.id))}
            />
          ))}
        </div>

        <FortuneDialog
          open={funBox === "fortune"}
          onOpenChange={(o) => { if (!o) setFunBox(null); }}
          fortuneIdx={fortuneIdx}
          onReroll={() => setFortuneIdx(Math.floor(Math.random() * 10000))}
        />
        <CoffeeDialog
          open={funBox === "coffee"}
          onOpenChange={(o) => { if (!o) setFunBox(null); }}
        />
      </AppShell>
    );
  }

  /* ---------------------------------------------------------- SECTION VIEWS */
  const currentTile = tiles.find((t) => t.id === section);
  const sectionHeader = (
    <div className="mb-4 flex items-center gap-3">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setSection(null)}
        data-testid="settings-section-back"
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        All settings
      </Button>
      <div className="text-sm font-medium text-muted-foreground">
        Settings · <span className="text-foreground">{currentTile?.label}</span>
      </div>
    </div>
  );

  // ---- DATA MANAGEMENT (sub-tile grid)
  if (section === "data") {
    const subTiles = [
      { to: "/admin/parks", label: "Parks", sublabel: "Geofences & timing", icon: TreePine, theme: "emerald", testid: "data-tile-parks" },
      { to: "/admin/park-assets", label: "Park Assets", sublabel: "Roads · OSR · Footpaths", icon: Layers, theme: "amber", testid: "data-tile-park-assets" },
      { to: "/admin/supervisors", label: "PMC Supervisors", sublabel: "Park assignments", icon: Users, theme: "blue", testid: "data-tile-supervisors" },
      { to: "/admin/contractors", label: "Contractors", sublabel: "Companies & users", icon: HardHat, theme: "amber", testid: "data-tile-contractors" },
      { to: "/admin/employees", label: "Employees", sublabel: "Workers & face data", icon: IdCard, theme: "violet", testid: "data-tile-employees" },
    ];
    return (
      <AppShell title="Settings" subtitle="Data management">
        {sectionHeader}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid="data-tile-grid">
          {subTiles.map((t) => (
            <Tile key={t.to} {...t} />
          ))}
        </div>

        {/* SIPCOT master data seeder */}
        <SipcotSeedCard />
      </AppShell>
    );
  }

  return (
    <AppShell title="Settings" subtitle={currentTile?.label}>
      {sectionHeader}

      {section === "windows" && (
        <Card>
          <CardContent className="p-5">
            <PerParkWindowsEditor settings={settings} onSaved={loadAll} />
          </CardContent>
        </Card>
      )}

      {section === "face" && (
        <Card className="max-w-2xl">
          <CardContent className="flex flex-col gap-3 p-5">
            <div className="flex items-center gap-2"><ScanFace className="h-5 w-5 text-primary" /><div className="font-display text-base font-semibold">Face-match threshold</div></div>
            <p className="text-xs text-muted-foreground">
              Lower = stricter (fewer false matches but more false rejects). Default <b>0.60</b> is balanced.
            </p>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between text-xs">
                <span>Strict</span>
                <span className="font-mono">{Number(settings.face_match_threshold).toFixed(2)}</span>
                <span>Lenient</span>
              </div>
              <Slider
                min={0.3}
                max={0.8}
                step={0.01}
                value={[Number(settings.face_match_threshold)]}
                onValueChange={(v)=>setSettings({...settings, face_match_threshold: v[0]})}
                data-testid="settings-threshold-slider"
              />
            </div>
            <div>
              <Button onClick={save} disabled={saving} size="sm" data-testid="settings-face-save">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {section === "salary-policy" && (
        <Card className="max-w-2xl">
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="flex items-center gap-2">
              <CalendarX2 className="h-5 w-5 text-primary" />
              <div className="font-display text-base font-semibold">Sunday-Leave Rule</div>
            </div>
            <p className="text-sm text-muted-foreground">
              If an employee is absent for STRICTLY MORE than{" "}
              <b className="text-foreground">{Number(settings.sunday_absent_threshold ?? 4)}</b>{" "}
              day(s) within the Mon&ndash;Sat span of a calendar week, the
              Sunday of that week is treated as <b className="text-rose-700">Absent (unpaid)</b>{" "}
              in the monthly Salary Report. This is applied automatically when
              the salary XLSX is generated.
            </p>
            <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 text-xs text-amber-900">
              <b>Humanitarian / Medical exception:</b> if you mark that
              specific Sunday as a <b>Holiday</b> (Settings &rarr; Holidays),
              the rule is skipped for that day and the Sunday remains paid.
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="sun-thr" className="text-xs uppercase tracking-wider text-muted-foreground">
                Max Mon&ndash;Sat absences before Sunday becomes unpaid
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="sun-thr"
                  type="number"
                  min={0}
                  max={6}
                  step={1}
                  className="w-28 font-mono tabular-nums"
                  value={Number(settings.sunday_absent_threshold ?? 4)}
                  onChange={(e) => setSettings({
                    ...settings,
                    sunday_absent_threshold: Math.max(0, Math.min(6, Number(e.target.value) || 0)),
                  })}
                  data-testid="settings-sunday-threshold-input"
                />
                <span className="text-xs text-muted-foreground">
                  Range 0&ndash;6 · Default <b>4</b> (i.e., 5+ absent days flip Sunday to A)
                </span>
              </div>
            </div>

            <div className="grid gap-2 rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              <div className="font-medium text-foreground">How it works (examples for threshold = 4)</div>
              <div>&bull; 4 absent days in Mon&ndash;Sat &rarr; Sunday stays <b className="text-emerald-700">S (paid)</b></div>
              <div>&bull; 5 absent days in Mon&ndash;Sat &rarr; Sunday flips to <b className="text-rose-700">A (unpaid)</b></div>
              <div>&bull; 6 absent days in Mon&ndash;Sat &rarr; Sunday flips to <b className="text-rose-700">A (unpaid)</b></div>
              <div>&bull; Any value &rarr; Sunday declared as Holiday stays <b className="text-emerald-700">S (paid)</b></div>
            </div>

            <div className="pt-1">
              <Button onClick={save} disabled={saving} size="sm" data-testid="settings-salary-policy-save">
                {saving ? "Saving…" : "Save policy"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {section === "hourly-selfie" && (
        <Card className="max-w-2xl">
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="flex items-center gap-2">
              <ScanFace className="h-5 w-5 text-primary" />
              <div className="font-display text-base font-semibold">Hourly Self-Attendance</div>
            </div>
            <p className="text-sm text-muted-foreground">
              ITCOT (PMC) supervisors capture a quick selfie during the working day so admin can track on-ground presence. Cadence is configured per-park (see Parks &rarr; edit park &rarr; &ldquo;Hourly check-in interval&rdquo;); these are the global fallback values.
            </p>

            <div className="flex items-center justify-between rounded-md border bg-muted/30 p-3">
              <div>
                <div className="text-sm font-medium">Feature enabled</div>
                <div className="text-xs text-muted-foreground">When off, PMC supervisors see no nudges and the admin grid is empty for new days.</div>
              </div>
              <Switch
                checked={settings.hourly_selfie_enabled !== false}
                onCheckedChange={(v) => setSettings({ ...settings, hourly_selfie_enabled: v })}
                data-testid="settings-hourly-enabled"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-1">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Default interval (minutes)
                </Label>
                <Input
                  type="number"
                  min={5}
                  max={180}
                  step={5}
                  value={Number(settings.hourly_selfie_default_interval_minutes ?? 60)}
                  onChange={(e) => setSettings({
                    ...settings,
                    hourly_selfie_default_interval_minutes: Math.max(5, Math.min(180, Number(e.target.value) || 60)),
                  })}
                  className="font-mono tabular-nums"
                  data-testid="settings-hourly-interval"
                />
                <span className="text-[10px] text-muted-foreground">Used when a park has no override</span>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Window start (IST)</Label>
                <Input
                  type="time"
                  value={settings.hourly_selfie_window_start || "08:00"}
                  onChange={(e) => setSettings({ ...settings, hourly_selfie_window_start: e.target.value })}
                  className="font-mono tabular-nums"
                  data-testid="settings-hourly-window-start"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Window end (IST)</Label>
                <Input
                  type="time"
                  value={settings.hourly_selfie_window_end || "18:00"}
                  onChange={(e) => setSettings({ ...settings, hourly_selfie_window_end: e.target.value })}
                  className="font-mono tabular-nums"
                  data-testid="settings-hourly-window-end"
                />
              </div>
            </div>

            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              <b className="text-foreground">Per-park override:</b> Open <Link to="/admin/parks" className="text-primary underline">Parks</Link> and edit a park to set its own cadence (busier parks may want 30m; remote parks 90m). When the supervisor manages multiple parks, the strictest interval wins.
            </div>

            <div className="pt-1">
              <Button onClick={save} disabled={saving} size="sm" data-testid="settings-hourly-save">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {section === "supervisor-gps-tracking" && (
        <Card className="max-w-2xl">
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              <div className="font-display text-base font-semibold">Supervisor Live Location Tracking</div>
            </div>
            <p className="text-sm text-muted-foreground">
              Configure the automated live location tracking interval for PMC Supervisors and Record Supervisors. The selected time interval is applied automatically to background movement pings.
            </p>

            <div className="flex flex-col gap-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Tracking Interval
              </Label>
              <Select
                value={String(settings.supervisor_gps_tracking_interval_minutes ?? 5)}
                onValueChange={(val) => setSettings({ ...settings, supervisor_gps_tracking_interval_minutes: Number(val) })}
              >
                <SelectTrigger className="w-64 bg-background" data-testid="settings-supervisor-gps-interval-select">
                  <SelectValue placeholder="Select interval…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 Minutes</SelectItem>
                  <SelectItem value="10">10 Minutes</SelectItem>
                  <SelectItem value="15">15 Minutes</SelectItem>
                  <SelectItem value="30">30 Minutes</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                Current selection: <b className="text-foreground">{settings.supervisor_gps_tracking_interval_minutes ?? 5} Minutes</b>
              </span>
            </div>

            <div className="pt-1">
              <Button onClick={save} disabled={saving} size="sm" data-testid="settings-supervisor-gps-save">
                {saving ? "Saving…" : "Save Interval"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {section === "categories" && (
        <Card>
          <CardContent className="flex flex-col gap-3 p-5">
            <div className="flex items-center gap-2"><Tag className="h-5 w-5 text-primary" /><div className="font-display text-base font-semibold">Worker categories</div></div>
            <p className="text-xs text-muted-foreground">These categories appear in the dropdown when adding a new employee. Defaults: Sweeper, Driver, Supervisor.</p>
            <div className="flex flex-wrap gap-2" data-testid="settings-categories-list">
              {(settings.worker_categories || []).map((c) => (
                <span key={c} className="inline-flex items-center gap-1 rounded-full border bg-secondary/60 pl-3 pr-1 py-1 text-xs" data-testid={`settings-category-chip-${c}`}>
                  {c}
                  <button type="button" onClick={() => removeCategory(c)} className="rounded-full p-1 hover:bg-secondary" aria-label={`Remove ${c}`} data-testid={`settings-category-remove-${c}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {(settings.worker_categories || []).length === 0 && <span className="text-xs text-muted-foreground">No categories defined.</span>}
            </div>
            <div className="flex max-w-md gap-2">
              <Input
                placeholder="Add a category (e.g., Gardener, Security)"
                value={newCategory}
                onChange={(e)=>setNewCategory(e.target.value)}
                onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
                data-testid="settings-new-category-input"
              />
              <Button onClick={addCategory} disabled={catBusy || !newCategory.trim()} data-testid="settings-add-category-button">
                <Plus className="mr-1.5 h-4 w-4" />Add
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {section === "credentials" && (
        <Card>
          <CardContent className="p-5">
            <CredentialsManager />
          </CardContent>
        </Card>
      )}

    </AppShell>
  );
}

/* ---------------------------------------------- per-park windows editor */
function PerParkWindowsEditor({ settings, onSaved }) {
  const [parks, setParks] = useState([]);
  const [parkId, setParkId] = useState("");
  const [busy, setBusy] = useState(false);

  const loadParks = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/parks");
      setParks(data);
      if (!parkId && data[0]) setParkId(data[0].id);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadParks();
    // eslint-disable-next-line
  }, []);

  const park = parks.find((p) => p.id === parkId);

  const summarize = (windows) => {
    if (!windows || windows.length === 0) return "global default";
    const labels = windows.filter((w) => w.active).map((w) => w.label);
    return labels.length === 0 ? "all inactive" : labels.join(" · ");
  };

  return (
    <div className="flex flex-col gap-5">
      <AttendanceWindowsMatrix settings={settings} onSaved={() => { loadParks(); onSaved?.(); }} />

      {parks.length === 0 && !busy && (
        <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
          Add a park first.
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------- SIPCOT seed card */
function SipcotSeedCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const runSeed = async () => {
    if (!window.confirm("Bulk-seed the SIPCOT master data?\n\nThis is idempotent — existing parks / employees are left alone. New parks get the approved-manpower counts from the manpower sheet, and missing employees are added (relievers auto-flagged for over-headcount).")) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/seed/sipcot-master");
      setResult(data.summary);
      toast.success("SIPCOT master data imported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-4 border-amber-200 bg-amber-50/30" data-testid="sipcot-seed-card">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-amber-200 bg-amber-100">
            <DatabaseBackup className="h-5 w-5 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-base font-semibold text-amber-900">SIPCOT master data</div>
            <div className="text-xs text-muted-foreground">
              One-shot bulk seed for the 24 SIPCOT parks + 473 master employees from the bundled spreadsheets.
              Approved headcount is taken from <span className="font-mono text-foreground">Manpower Details.xlsx</span> and
              workers above that count per category are auto-flagged as <b>Relievers</b>. Safe to re-run — only missing rows
              are inserted.
            </div>
          </div>
          <Button
            onClick={runSeed}
            disabled={busy}
            size="sm"
            data-testid="sipcot-seed-button"
            className="shrink-0"
          >
            <Sparkles className="mr-1.5 h-4 w-4" />
            {busy ? "Seeding…" : "Run seed"}
          </Button>
        </div>

        {result && (
          <div className="rounded-md border border-amber-200 bg-white p-3 text-xs" data-testid="sipcot-seed-result">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
              <SeedStat label="Contractors inserted" v={result.contractors_inserted} />
              <SeedStat label="Parks inserted" v={result.parks_inserted} />
              <SeedStat label="Parks updated" v={result.parks_updated} />
              <SeedStat label="Employees inserted" v={result.employees_inserted} />
              <SeedStat label="Skipped (already there)" v={result.employees_skipped_existing} />
              <SeedStat label="Relievers flagged" v={result.relievers_flagged} accent="rose" />
              <SeedStat label="Supervisor logins created" v={result.supervisor_logins_created} accent="emerald" />
              <SeedStat label="Supervisor logins skipped" v={result.supervisor_logins_skipped} />
            </div>
            {result.supervisor_logins_created > 0 && (
              <div className="mt-2 rounded bg-emerald-50 p-2 text-emerald-900">
                <span className="font-semibold">Default credentials:</span> username = employee ID, password = employee ID (e.g. <span className="font-mono">10449 / 10449</span>). Reset from <span className="font-medium">Settings → User Credentials</span> or via Change Password.
              </div>
            )}
            {result.employees_unmatched_park > 0 && (
              <div className="mt-2 rounded bg-rose-50 p-2 text-rose-800">
                {result.employees_unmatched_park} employees had a park name not found in the manpower sheet. Sample: {(result.unmatched_park_samples || []).join(", ")}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SeedStat({ label, v, accent = "amber" }) {
  const color =
    accent === "rose" ? "text-rose-700"
    : accent === "emerald" ? "text-emerald-700"
    : "text-amber-800";
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono font-semibold tabular-nums ${color}`}>{v ?? 0}</span>
    </div>
  );
}


/* =========================================================== FUN DIALOGS */
// Light-hearted easter eggs to fill the empty corner of the Settings grid.
// Both dialogs are pure client-side (no API calls) so they can't fail.

const FORTUNES = [
  "A captured selfie is worth a thousand attendance disputes.",
  "Today's mismatch is tomorrow's training data.",
  "Behind every clean park stands a supervisor who showed up before sunrise.",
  "The strongest geofence is the one you never have to enforce.",
  "GPS coordinates don't lie. People sometimes do.",
  "Garbage in, garbage out — quite literally, in this line of work.",
  "May your Mondays start at 9:00 and your reports finish themselves.",
  "Plant a tree, capture an attendance, repeat. The kingdom prospers.",
  "A park well-swept is a park well-loved.",
  "Counting workers, counting hours, counting blessings.",
  "There is no Wi-Fi in the wilderness, only signal strength of the soul.",
  "Always tip the broom-keeper. Karma is real.",
  "If the camera saw it, it happened. If the camera blinked, ask again.",
  "Discipline is what happens when no one is watching, but the geofence still is.",
  "A 5-minute delay in the morning is a 50-minute meeting in the evening.",
  "Trust, but verify. With a timestamp. And a selfie.",
  "Behind every great salary report stands an even greater wage register.",
  "An audit a day keeps the accountant okay.",
  "Don't fear the mismatch. Embrace it. Train the model. Move on.",
  "Coffee first, compliance second. Spreadsheets third. Always.",
];

const COFFEE_LEVELS = [
  { min: 0,   max: 1,   emoji: "😴", line: "Zero coffee consumed. The team must still be commuting." },
  { min: 1,   max: 10,  emoji: "☕", line: "A timid sip. Could be the watchman warming up." },
  { min: 10,  max: 50,  emoji: "☕☕", line: "Mild buzz detected. Reports are being typed." },
  { min: 50,  max: 150, emoji: "☕☕☕", line: "Solid throughput. Spreadsheets are flowing." },
  { min: 150, max: 400, emoji: "☕☕☕☕", line: "High caffeine alert. Audit trail is on fire." },
  { min: 400, max: Infinity, emoji: "☕☕☕☕☕☕", line: "We have entered the productivity stratosphere." },
];

function FortuneDialog({ open, onOpenChange, fortuneIdx, onReroll }) {
  const fortune = FORTUNES[fortuneIdx % FORTUNES.length];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="fortune-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-rose-600" /> Park-Master's Fortune
          </DialogTitle>
        </DialogHeader>
        <div className="rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50 to-amber-50 p-5 text-center">
          <div className="text-4xl">🥠</div>
          <div className="mt-3 text-[15px] font-medium leading-relaxed text-slate-800">
            "{fortune}"
          </div>
        </div>
        <DialogFooter className="sm:justify-between">
          <span className="text-[11px] text-slate-400">Fortune #{(fortuneIdx % FORTUNES.length) + 1} of {FORTUNES.length}</span>
          <Button onClick={onReroll} variant="outline" size="sm" data-testid="fortune-reroll">
            <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Crack another one
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CoffeeDialog({ open, onOpenChange }) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        // Re-use the dashboard payload — it's tiny and authenticated.
        const r = await api.get("/dashboard/admin").then((x) => x.data);
        if (!alive) return;
        const c = (r?.pmc_park_visits?.visited || 0) + (r?.contractor_park_visits?.visited || 0);
        // Each visit ≈ 3 cups of coffee. Important industry research.
        setCount(c * 3);
      } catch {
        if (alive) setCount(0);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const level = COFFEE_LEVELS.find((l) => (count ?? 0) >= l.min && (count ?? 0) < l.max) || COFFEE_LEVELS[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="coffee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coffee className="h-4 w-4 text-amber-700" /> Coffee Break Counter
          </DialogTitle>
        </DialogHeader>
        <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-5 text-center">
          <div className="text-5xl" data-testid="coffee-emoji">{level.emoji}</div>
          <div className="mt-3 text-[12px] uppercase tracking-wide text-amber-700/80">Estimated cups today</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-amber-900" data-testid="coffee-count">
            {count === null ? "…" : count}
          </div>
          <div className="mt-3 text-[13px] leading-relaxed text-slate-700">{level.line}</div>
          <div className="mt-3 text-[10px] text-slate-400">
            * Math: 1 park visit ≈ 3 cups. Industry standard. Mostly.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
